import { IndentedFormFieldContainer } from '@ui-components/global/FormStyling'
import { AdditionalText } from '@ui-components/typography/Typography'
import { MouseEvent } from 'react'
import { styled } from 'styled-components'

import { Button } from '../actions/Button'
import { TrashIcon } from '../assets/Icons'
import { ProgressBar } from '../decorative/ProgressBar'
import { Colors } from '../global/colors'
import { SPACE_MEDIUM } from '../global/spacing'
import { StorageDataAbbreviation } from '../types'
import { formatBytes } from '../utilities/textHelpers'
import { TruncatedInMiddleSpan } from './primitive/TruncatedInMiddleSpan'

const UploadItemContainer = styled(IndentedFormFieldContainer)`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 0 10px;
`

const LeftColumnContainer = styled.div`
  flex-grow: 1;
  overflow: hidden;
`

const TitleText = styled.button`
  display: block;
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  width: 100%;
  cursor: unset;
  pointer-events: none;
`

const ErrorAdditionalText = styled(AdditionalText)`
  color: ${Colors.TextError};
`

const ProgressBarContainer = styled.div`
  height: ${SPACE_MEDIUM};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
`

const ListItem = styled.li`
  list-style-type: none;
`

interface FormUploadItemProps {
  fileName: string
  fileSize: number
  onRemoveButtonClick: () => void
  isValid: boolean
  dataAbbreviation: StorageDataAbbreviation
  progressBarAriaLabel: string
  removeFileAriaLabel: string
  error?: string
  uploadProgress?: number
  loading?: boolean
  onClick?: () => void
  downloadFileAriaLabel?: string
}

export const FormUploadItem = ({
  fileName,
  fileSize,
  onRemoveButtonClick,
  error,
  uploadProgress = 100,
  isValid,
  dataAbbreviation,
  progressBarAriaLabel,
  removeFileAriaLabel,
  loading,
  onClick,
  downloadFileAriaLabel,
}: FormUploadItemProps) => {
  const isUploadDone = uploadProgress >= 100

  const resolveStatus = () => {
    if (error) {
      return <ErrorAdditionalText>{error}</ErrorAdditionalText>
    }

    if (isUploadDone) {
      return <AdditionalText>{formatBytes(fileSize, 2, dataAbbreviation)}</AdditionalText>
    }

    return (
      <ProgressBarContainer>
        <ProgressBar progress={uploadProgress} ariaLabel={progressBarAriaLabel} />
      </ProgressBarContainer>
    )
  }

  const isValidItem = isValid && isUploadDone
  const isValidAndNotBeingDeletedItem = isValidItem && !loading
  const isClickable = isValidAndNotBeingDeletedItem && !!onClick

  const deleteButtonTabIndex = () => {
    if (loading) {
      return -1
    }
    return isValidAndNotBeingDeletedItem ? 1 : 0
  }

  return (
    <ListItem>
      <UploadItemContainer>
        <LeftColumnContainer>
          <TitleText
            tabIndex={isValidAndNotBeingDeletedItem ? 0 : -1}
            as={isClickable ? 'button' : 'div'}
            onClick={
              isClickable
                ? (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault()
                    onClick?.()
                  }
                : undefined
            }
          >
            <TruncatedInMiddleSpan
              text={fileName}
              isLink={isClickable}
              isLinkAriaLabel={downloadFileAriaLabel}
              isValid={isValidItem}
            />
          </TitleText>
          {resolveStatus()}
        </LeftColumnContainer>
        {isUploadDone ? (
          <Button
            tabIndex={deleteButtonTabIndex()}
            onClick={onRemoveButtonClick}
            iconOnly={true}
            label={removeFileAriaLabel}
            icon={<TrashIcon />}
            variant="secondary"
            color="neutral"
            loadingMode="icon"
            loading={loading}
          />
        ) : null}
      </UploadItemContainer>
    </ListItem>
  )
}
